import React, { ChangeEvent, JSX, useCallback, useState } from 'react';

import { Box, Button, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import InputRateGroupMultiselect from '~/components/Cruises/components/Selects/InputRateGroupMultiselect';

import { CruiseLine } from '~/services/cruises/CruiseLineService';
import { RateGroup } from '~/services/cruises/RateGroupService';

import { SearchValues } from '../types';

type Props = {
  loading: boolean;
  initialVendorFilter?: CruiseLine;
  searchValues: SearchValues;
  setSearchValues: (searchValues: SearchValues) => void;
  onSearch: () => void;
};

function RateCodeManagementSearch({
  loading,
  initialVendorFilter,
  searchValues,
  setSearchValues,
  onSearch,
}: Props): JSX.Element {
  const [cruiseLine, setCruiseLine] = useState<CruiseLine>(initialVendorFilter);

  const handleChangeField = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.persist) {
        e.persist();
      }

      if (e?.target && e?.target?.name) {
        setSearchValues({
          ...searchValues,
          [e.target.name]: e.target.value,
        });
      }
    },
    [searchValues, setSearchValues],
  );

  const handleCruiseLineChange = useCallback(
    (cruiseLine: CruiseLine) => {
      setCruiseLine(cruiseLine);
      setSearchValues({
        ...searchValues,
        vendorCode: cruiseLine?.code,
        isInRateGroup: 'all',
        rateGroupIds: [],
      });
    },
    [setCruiseLine, setSearchValues, searchValues],
  );

  const handleChangeRateGroupChange = useCallback(
    (rateGroups: Array<RateGroup>) => {
      setSearchValues({
        ...searchValues,
        rateGroupIds: rateGroups.map((rateGroup) => rateGroup.id),
      });
    },
    [setSearchValues, searchValues],
  );

  return (
    <Stack direction="column" gap={2}>
      <Stack direction="row" spacing={2}>
        <Box flexGrow={1}>
          <TextField
            fullWidth
            name="rateCode"
            label="Rate Code"
            value={searchValues.rateCode}
            onChange={handleChangeField}
            placeholder="Use comma to search multiple rate codes (DSC, BESTFARE)"
          />
        </Box>

        <Box flexGrow={2}>
          <InputCruiseLineSelect
            label="Cruise Line"
            value={cruiseLine?.id}
            onChange={handleCruiseLineChange}
            disabled={!!initialVendorFilter}
            allowEmpty
            withApprovedOffers
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Box flexGrow={1}>
          <InputLabel>Status</InputLabel>
          <Select
            fullWidth
            name="enabledFilter"
            label="Status"
            value={searchValues.enabledFilter}
            onChange={handleChangeField}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Enabled</MenuItem>
            <MenuItem value="false">Disabled</MenuItem>
          </Select>
        </Box>

        <Box flexGrow={1}>
          <InputLabel>Is Net Rate?</InputLabel>
          <Select
            fullWidth
            name="netRateFilter"
            label="Is Net Rate?"
            value={searchValues.netRateFilter}
            onChange={handleChangeField}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </Box>

        <Box flexGrow={1}>
          <InputLabel>Is Flash?</InputLabel>
          <Select
            fullWidth
            name="flashFilter"
            label="Is Flash?"
            value={searchValues.flashFilter}
            onChange={handleChangeField}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </Box>

        {cruiseLine?.id && (
          <Box flexGrow={1}>
            <InputLabel>Has Rate Group</InputLabel>
            <Select
              fullWidth
              name="isInRateGroup"
              label="Rate Groups"
              value={searchValues.isInRateGroup}
              onChange={handleChangeField}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </Box>
        )}

        {cruiseLine?.id && searchValues.isInRateGroup === 'true' && (
          <Box flexGrow={1}>
            <InputLabel>Rate Groups</InputLabel>
            <InputRateGroupMultiselect
              label="Rate Groups"
              rateGroupIds={searchValues.rateGroupIds}
              vendorId={cruiseLine.id}
              onChange={handleChangeRateGroupChange}
            />
          </Box>
        )}
      </Stack>

      <Box>
        <Button variant="contained" disabled={loading} onClick={onSearch}>
          Search Content
        </Button>
      </Box>
    </Stack>
  );
}

export default RateCodeManagementSearch;
